/* ==========================================================================
   KASHTAN — UI behaviours
   Header + mobile drawer, announcement rotator, review slider, size/variant
   picker, and an add-to-cart that works on any theme (no cart-drawer coupling).
   ========================================================================== */
(function () {
  'use strict';

  if (window.KashtanUI) return;
  var UI = {};
  var routes = (window.Shopify && window.Shopify.routes) || {};
  var CART_ADD = (routes.root || '/') + 'cart/add.js';
  var CART_GET = (routes.root || '/') + 'cart.js';

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------------- header */

  function initHeader(scope) {
    $$('[data-k-header]', scope).forEach(function (header) {
      if (header.dataset.kInit) return;
      header.dataset.kInit = '1';

      var threshold = 24;
      var lastY = window.scrollY;

      /* The overlay layout pulls the hero up under the header with a negative
         margin equal to the header's height. That height changes with the logo
         width, so it has to be measured rather than hardcoded — otherwise
         raising the logo size breaks the transparent effect. */
      /* Shopify wraps each section in <div id="shopify-section-…">. That wrapper
         is where the stickiness lives (see .k-header-sticky), so the measured
         height and the overlay pull-up have to be written there too. */
      var wrap = header.parentElement;
      if (!wrap || !wrap.classList.contains('k-header-sticky')) wrap = header;
      if (header.classList.contains('k-header--overlay')) {
        wrap.classList.add('k-header-sticky--overlay');
      }

      function measure() {
        var h = header.offsetHeight;
        if (!h) return;
        header.style.setProperty('--k-header-h', h + 'px');
        wrap.style.setProperty('--k-header-h', h + 'px');
      }
      measure();
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('load', measure);
      // The logo is usually the tallest thing in the bar and loads late.
      header.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', measure, { once: true });
      });
      if (window.ResizeObserver) new ResizeObserver(measure).observe(header);

      // "On scroll" in the section settings: reveal | sticky | static
      var mode = header.getAttribute('data-k-scroll') || 'reveal';
      if (header.hasAttribute('data-k-header-hide')) mode = 'reveal';
      if (mode === 'static') header.classList.add('k-header--static');

      function onScroll() {
        var y = window.scrollY;
        header.classList.toggle('is-stuck', y > threshold);

        if (mode === 'reveal') {
          /* ANY upward movement reveals the header, anywhere on the page —
             including a 1px nudge and the bounce at the bottom. Only a
             sustained downward scroll past the fold hides it. */
          if (y < lastY || y <= 8) {
            header.classList.remove('is-hidden');
          } else if (y > lastY + 6 && y > 260) {
            header.classList.add('is-hidden');
          }
        } else {
          header.classList.remove('is-hidden');
        }

        lastY = y < 0 ? 0 : y;
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      // Mobile drawer
      var toggle = $('[data-k-drawer-open]', header);
      var drawer = $('[data-k-drawer]', header);
      if (!toggle || !drawer) return;

      var closers = $$('[data-k-drawer-close]', header);
      var lastFocus = null;
      var raf = null;
      var hideTimer = null;

      function open() {
        lastFocus = document.activeElement;
        window.clearTimeout(hideTimer);
        drawer.hidden = false;
        // Next frame so the transition has a start value to animate from.
        // Tracked so a close() in the same frame can cancel it.
        raf = requestAnimationFrame(function () {
          raf = null;
          header.classList.add('is-open');
        });
        document.documentElement.style.overflow = 'hidden';
        toggle.setAttribute('aria-expanded', 'true');
        var first = $('a, button', drawer);
        if (first) first.focus();
      }
      function close() {
        if (raf !== null) { cancelAnimationFrame(raf); raf = null; }
        header.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        toggle.setAttribute('aria-expanded', 'false');
        window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(function () { drawer.hidden = true; }, 350);
        if (lastFocus) lastFocus.focus();
      }

      toggle.addEventListener('click', function () {
        header.classList.contains('is-open') ? close() : open();
      });
      closers.forEach(function (el) { el.addEventListener('click', close); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && header.classList.contains('is-open')) close();
      });

      // Collapsible sub-menus inside the drawer
      $$('[data-k-submenu-toggle]', drawer).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var expanded = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!expanded));
          var panel = btn.nextElementSibling;
          if (panel) panel.hidden = expanded;
        });
      });
    });
  }

  /* --------------------------------------------------- announcement rotator */

  function initAnnouncement(scope) {
    $$('[data-k-rotate]', scope).forEach(function (el) {
      if (el.dataset.kInit) return;
      el.dataset.kInit = '1';

      var items = $$('[data-k-rotate-item]', el);
      if (items.length < 2) return;
      var every = parseInt(el.getAttribute('data-k-rotate') || '5000', 10);
      var i = 0;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      setInterval(function () {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, every);
    });
  }

  /* -------------------------------------------------------- review slider */

  function initSlider(scope) {
    $$('[data-k-slider]', scope).forEach(function (root) {
      if (root.dataset.kInit) return;
      root.dataset.kInit = '1';

      var track = $('[data-k-slider-track]', root);
      var slides = $$('[data-k-slide]', track);
      var prev = $('[data-k-slider-prev]', root);
      var next = $('[data-k-slider-next]', root);
      var dotsWrap = $('[data-k-slider-dots]', root);
      if (!track || slides.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        return;
      }

      var dots = [];
      if (dotsWrap) {
        slides.forEach(function (_, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'k-dot';
          b.setAttribute('aria-label', 'Go to review ' + (i + 1));
          b.addEventListener('click', function () { goTo(i); });
          dotsWrap.appendChild(b);
          dots.push(b);
        });
      }

      function current() {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0, bestDist = Infinity;
        slides.forEach(function (s, i) {
          var c = s.offsetLeft + s.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      }

      function goTo(i) {
        var target = slides[Math.max(0, Math.min(i, slides.length - 1))];
        track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }

      function sync() {
        var i = current();
        dots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      }

      if (prev) prev.addEventListener('click', function () { goTo(current() - 1); });
      if (next) next.addEventListener('click', function () { goTo(current() + 1); });
      track.addEventListener('scroll', function () {
        window.clearTimeout(track._kT);
        track._kT = window.setTimeout(sync, 90);
      }, { passive: true });
      sync();
    });
  }

  /* ------------------------------------------------- horizontal product rail */

  function initRail(scope) {
    $$('[data-k-rail]', scope).forEach(function (root) {
      if (root.dataset.kInit) return;
      root.dataset.kInit = '1';

      var track = $('[data-k-rail-track]', root);
      // Controls usually live in the section header, outside the rail wrapper.
      var section = root.closest('.k-section') || root.parentNode;
      var prev = $('[data-k-rail-prev]', section);
      var next = $('[data-k-rail-next]', section);
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        var gap = parseFloat(getComputedStyle(track).columnGap || '24') || 24;
        return first ? first.offsetWidth + gap : track.clientWidth * 0.8;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        var overflowing = max > 2;
        if (prev) { prev.disabled = track.scrollLeft <= 2; prev.hidden = !overflowing; }
        if (next) { next.disabled = track.scrollLeft >= max; next.hidden = !overflowing; }
      }

      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });

      track.addEventListener('scroll', function () {
        window.clearTimeout(track._kT);
        track._kT = window.setTimeout(sync, 80);
      }, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* --------------------------------------- hero lockup: fade out on scroll */

  function initLockup(scope) {
    $$('.k-hero__lockup--fade', scope).forEach(function (el) {
      if (el.dataset.kFadeInit) return;
      el.dataset.kFadeInit = '1';

      var hero = el.closest('.k-hero');
      if (!hero) return;
      var frame = null;

      function update() {
        frame = null;
        var h = hero.offsetHeight || 1;
        // fully visible at the top, gone by the time 65% of the hero has passed
        var p = Math.min(Math.max(window.scrollY / (h * 0.65), 0), 1);
        el.style.setProperty('--k-lockup-o', (1 - p).toFixed(3));
      }

      window.addEventListener('scroll', function () {
        if (frame) return;
        frame = requestAnimationFrame(update);
      }, { passive: true });
      update();
    });
  }

  /* ------------------------------------------------- card cursor tracking */

  function initCardTrack(scope) {
    if (window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    $$('[data-k-card-track]', scope).forEach(function (media) {
      if (media.dataset.kInit) return;
      media.dataset.kInit = '1';

      var img = $('.k-card__img', media);
      if (!img) return;
      var frame = null;

      // The image scales around the cursor point (transform-origin), so the
      // pixel under the cursor never moves and no edge can be exposed however
      // large the zoom. Origin is set on mouseenter *before* the hover scale
      // applies, so an instant zoom lands in the right place first time.
      function setOrigin(e) {
        var r = media.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var ox = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
        var oy = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
        media.style.setProperty('--k-ox', ox.toFixed(2) + '%');
        media.style.setProperty('--k-oy', oy.toFixed(2) + '%');
      }

      media.addEventListener('mouseenter', setOrigin);

      media.addEventListener('mousemove', function (e) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          setOrigin(e);
        });
      });

      media.addEventListener('mouseleave', function () {
        media.style.removeProperty('--k-ox');
        media.style.removeProperty('--k-oy');
      });
    });
  }

  /* ------------------------------------------------------ product gallery */

  function initGallery(scope) {
    $$('[data-k-pgal]', scope).forEach(function (root) {
      if (root.dataset.kInit) return;
      root.dataset.kInit = '1';

      var thumbs = $$('[data-k-pgal-thumb]', root);
      var slides = $$('[data-k-pgal-slide]', root);
      if (!thumbs.length || !slides.length) return;
      var hover = root.getAttribute('data-k-pgal-hover') === 'true' &&
                  !window.matchMedia('(hover: none)').matches;

      function show(i) {
        thumbs.forEach(function (t, n) {
          var on = n === i;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        slides.forEach(function (sl, n) {
          var on = n === i;
          sl.classList.toggle('is-active', on);
          if (on) sl.removeAttribute('aria-hidden');
          else sl.setAttribute('aria-hidden', 'true');
        });
      }

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () { show(i); });
        t.addEventListener('focus', function () { show(i); });
        if (hover) t.addEventListener('mouseenter', function () { show(i); });
      });

      // Same loupe behaviour as a product card: scale around the cursor
      // point so nothing slides out of frame at high zoom.
      if (window.matchMedia('(hover: none)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      $$('[data-k-pgal-zoom]', root).forEach(function (frame) {
        var raf = null;
        function origin(e) {
          var r = frame.getBoundingClientRect();
          if (!r.width || !r.height) return;
          var ox = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
          var oy = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
          frame.style.setProperty('--k-ox', ox.toFixed(2) + '%');
          frame.style.setProperty('--k-oy', oy.toFixed(2) + '%');
        }
        frame.addEventListener('mouseenter', origin);
        frame.addEventListener('mousemove', function (e) {
          if (raf) return;
          raf = requestAnimationFrame(function () { raf = null; origin(e); });
        });
        frame.addEventListener('mouseleave', function () {
          frame.style.removeProperty('--k-ox');
          frame.style.removeProperty('--k-oy');
        });
      });
    });
  }

  /* ---------------------------------------------------------- size picker */

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (format && format.indexOf('{{') > -1) {
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  function initVariantPicker(scope) {
    $$('[data-k-variants]', scope).forEach(function (root) {
      if (root.dataset.kInit) return;
      root.dataset.kInit = '1';

      var dataEl = $('[data-k-variant-json]', root);
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var idInput = $('[data-k-variant-id]', root);
      var priceEl = $('[data-k-variant-price]', root);
      var compareEl = $('[data-k-variant-compare]', root);
      var submit = $('[data-k-submit]', root);
      var format = root.getAttribute('data-k-money-format') || '';
      var updateUrl = root.hasAttribute('data-k-update-url');

      // Price per unit, rendered by Liquid per variant (kashtan-price snippet)
      var unitEl = $('[data-k-variant-unit]', root);
      var unitMode = root.getAttribute('data-k-unit-mode') || 'replace';
      var units = {};
      var unitJson = $('[data-k-unit-json]', root);
      if (unitJson) { try { units = JSON.parse(unitJson.textContent) || {}; } catch (e) { units = {}; } }

      function apply(variant) {
        if (!variant) return;
        if (idInput) idInput.value = variant.id;
        var unit = units[variant.id] || units[String(variant.id)] || '';
        var perOnly = !!unit && unitMode === 'replace';
        if (priceEl) priceEl.textContent = perOnly ? unit : money(variant.price, format);
        if (unitEl) {
          unitEl.textContent = unit;
          unitEl.hidden = !unit || perOnly;
        }
        if (compareEl && perOnly) {
          compareEl.hidden = true;
        } else if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = money(variant.compare_at_price, format);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available
            ? (submit.getAttribute('data-k-label') || 'Add to cart')
            : (submit.getAttribute('data-k-label-sold') || 'Sold out');
        }
        if (updateUrl && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
        root.dispatchEvent(new CustomEvent('kashtan:variant', { bubbles: true, detail: { variant: variant } }));
      }

      $$('[data-k-variant-option]', root).forEach(function (input) {
        input.addEventListener('change', function () {
          var id = parseInt(input.value, 10);
          var match = variants.filter(function (v) { return v.id === id; })[0];
          apply(match);
        });
      });

      var checked = $('[data-k-variant-option]:checked', root);
      if (checked) {
        var id = parseInt(checked.value, 10);
        apply(variants.filter(function (v) { return v.id === id; })[0]);
      }
    });
  }

  /* -------------------------------------------------------------- add to cart */

  function toast(message) {
    var el = document.createElement('div');
    el.className = 'k-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    setTimeout(function () {
      el.classList.remove('is-in');
      setTimeout(function () { el.remove(); }, 400);
    }, 3200);
  }

  function refreshCartCount() {
    fetch(CART_GET, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-k-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
        });
        document.dispatchEvent(new CustomEvent('kashtan:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {});
  }

  function initAddToCart(scope) {
    $$('[data-k-cart-form]', scope).forEach(function (form) {
      if (form.dataset.kInit) return;
      form.dataset.kInit = '1';

      form.addEventListener('submit', function (e) {
        if (!window.fetch) return; // let the browser post normally
        e.preventDefault();

        var btn = $('[data-k-submit]', form);
        var label = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

        fetch(CART_ADD, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.data.description || res.data.message || 'Could not add to cart');
            toast(form.getAttribute('data-k-added-text') || 'Added to your order');
            refreshCartCount();
          })
          .catch(function (err) { toast(err.message); })
          .then(function () {
            if (btn) { btn.disabled = false; btn.textContent = label; }
          });
      });
    });
  }

  /* --------------------------------------------------------------- accordion */

  function initAccordion(scope) {
    // Native <details> handles state; we only animate the panel height.
    $$('[data-k-accordion] details', scope).forEach(function (d) {
      if (d.dataset.kInit) return;
      d.dataset.kInit = '1';
      var group = d.closest('[data-k-accordion]');
      if (group && group.hasAttribute('data-k-accordion-single')) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          $$('details', group).forEach(function (other) {
            if (other !== d) other.open = false;
          });
        });
      }
    });
  }

  /* -------------------------------------------------------------------- init */

  function init(scope) {
    scope = scope || document;
    initHeader(scope);
    initAnnouncement(scope);
    initSlider(scope);
    initRail(scope);
    initGallery(scope);
    initCardTrack(scope);
    initLockup(scope);
    initVariantPicker(scope);
    initAddToCart(scope);
    initAccordion(scope);
  }

  UI.init = init;
  UI.toast = toast;
  UI.refreshCartCount = refreshCartCount;
  window.KashtanUI = UI;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target && e.target.querySelectorAll ? e.target : document);
  });
})();
